// Local Settings Store: dados globais como fornecedores, marcas, modelos e materiais
// Persistência em localStorage. Simples, sem dependência de backend.

// Utilitários de normalização
const normalizeWhitespace = (str) => String(str || '')
  .replace(/\s+/g, ' ')
  .trim();

const knownAcronyms = new Set(['PLA','ABS','PETG','TPU','ASA','PC','PVA','HIPS','PEEK','PEI','PA','PA-CF','PA-GF','CF','GF','3D']);
export const normalizeCase = (str) => {
  const s = normalizeWhitespace(str);
  if (!s) return '';
  return s.split(' ').map(w => {
    const clean = w;
    if (knownAcronyms.has(clean.toUpperCase())) return clean.toUpperCase();
    return clean.charAt(0).toUpperCase() + clean.slice(1).toLowerCase();
  }).join(' ');
};
const toKey = (str) => normalizeWhitespace(str).toLowerCase();

const LS_KEY = 'global_settings';

const safeParse = (str, fallback) => {
  try { return JSON.parse(str ?? ''); } catch { return fallback; }
};

const loadSettings = () => {
  if (typeof window === 'undefined') return getDefaultSettings();
  const raw = window.localStorage.getItem(LS_KEY);
  const parsed = safeParse(raw, null);
  if (!parsed || typeof parsed !== 'object') {
    const defaults = getDefaultSettings();
    saveSettings(defaults);
    return defaults;
  }
  // Garantir chaves obrigatórias
  const base = getDefaultSettings();
  return { ...base, ...parsed };
};

const saveSettings = (settings) => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LS_KEY, JSON.stringify(settings || getDefaultSettings()));
};

const uniquePush = (arr, value) => {
  const v = String(value || '').trim();
  if (!v) return arr;
  if (!Array.isArray(arr)) arr = [];
  if (arr.some((x) => String(x).trim().toLowerCase() === v.toLowerCase())) return arr;
  return [...arr, v];
};

const getDefaultSettings = () => ({
  suppliers: [],
  printerModels: [],
  printerBrands: [],
  filamentBrands: [],
  printerModelsByBrand: {},
  materialTypes: [
    { name: 'PLA', density: 1.24 },
    { name: 'ABS', density: 1.04 },
    { name: 'PETG', density: 1.27 },
    { name: 'TPU', density: 1.21 },
    { name: 'ASA', density: 1.07 },
    { name: 'PC', density: 1.20 },
    { name: 'Nylon', density: 1.13 },
    { name: 'HIPS', density: 1.04 }
  ],
});

export const settingsStore = {
  // Leitura completa
  async getSettings() {
    try {
      const data = loadSettings();
      return { data, error: null };
    } catch (error) {
      return { data: getDefaultSettings(), error };
    }
  },

  // Fornecedores
  async getSuppliers() {
    try {
      const s = loadSettings();
      return { data: Array.isArray(s.suppliers) ? s.suppliers : [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },
  async createSupplier(name) {
    try {
      const s = loadSettings();
      const n = normalizeCase(name);
      s.suppliers = uniquePush(s.suppliers, n);
      saveSettings(s);
      return { data: n, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },
  async updateSupplier(oldName, newName) {
    try {
      const s = loadSettings();
      const old = normalizeCase(oldName);
      const neu = normalizeCase(newName);
      if (!old || !neu) return { data: null, error: new Error('Nome inválido') };
      s.suppliers = (Array.isArray(s.suppliers) ? s.suppliers : [])
        .map((x) => (toKey(x) === toKey(old) ? neu : normalizeCase(x)))
        .filter(Boolean);
      saveSettings(s);
      return { data: neu, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },
  async deleteSupplier(name) {
    try {
      const s = loadSettings();
      const target = toKey(name);
      s.suppliers = (Array.isArray(s.suppliers) ? s.suppliers : [])
        .filter((x) => toKey(x) !== target);
      saveSettings(s);
      return { data: name, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },
  async mergeSuppliers(list) {
    try {
      const s = loadSettings();
      const current = Array.isArray(s.suppliers) ? s.suppliers : [];
      const map = new Map(current.map(v => [toKey(v), normalizeCase(v)]));
      (Array.isArray(list) ? list : []).forEach(item => {
        const norm = normalizeCase(item);
        const key = toKey(norm);
        if (!map.has(key)) map.set(key, norm);
      });
      s.suppliers = Array.from(map.values()).sort((a,b)=>a.localeCompare(b));
      saveSettings(s);
      return { data: s.suppliers, error: null };
    } catch (error) { return { data: [], error }; }
  },

  // Marcas de impressora
  async getPrinterBrands() {
    try {
      const s = loadSettings();
      return { data: Array.isArray(s.printerBrands) ? s.printerBrands : [], error: null };
    } catch (error) { return { data: [], error }; }
  },
  async createPrinterBrand(name) {
    try { const s = loadSettings(); const n = normalizeCase(name); s.printerBrands = uniquePush(s.printerBrands, n); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; }
  },
  async updatePrinterBrand(oldName, newName) {
    try { const s = loadSettings(); const o=normalizeCase(oldName); const n=normalizeCase(newName); s.printerBrands = (s.printerBrands||[]).map(x=>toKey(x)===toKey(o)?n:normalizeCase(x)); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; }
  },
  async deletePrinterBrand(name) {
    try { const s = loadSettings(); const t=toKey(name); s.printerBrands=(s.printerBrands||[]).filter(x=>toKey(x)!==t); saveSettings(s); return { data: name, error: null }; } catch (error) { return { data: null, error }; }
  },
  async mergePrinterBrands(list) {
    try { const s = loadSettings(); const current = Array.isArray(s.printerBrands)?s.printerBrands:[]; const map = new Map(current.map(v=>[toKey(v), normalizeCase(v)])); (Array.isArray(list)?list:[]).forEach(item=>{ const norm=normalizeCase(item); const key=toKey(norm); if(!map.has(key)) map.set(key,norm); }); s.printerBrands = Array.from(map.values()).sort((a,b)=>a.localeCompare(b)); saveSettings(s); return { data: s.printerBrands, error: null }; } catch (error) { return { data: [], error }; }
  },

  // Modelos de impressora
  async getPrinterModels() { try { const s = loadSettings(); return { data: Array.isArray(s.printerModels)?s.printerModels:[], error: null }; } catch (error) { return { data: [], error }; } },
  async createPrinterModel(name) { try { const s = loadSettings(); const n = normalizeCase(name); s.printerModels = uniquePush(s.printerModels, n); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; } },
  async updatePrinterModel(oldName, newName) { try { const s = loadSettings(); const o=normalizeCase(oldName); const n=normalizeCase(newName); s.printerModels=(s.printerModels||[]).map(x=>toKey(x)===toKey(o)?n:normalizeCase(x)); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; } },
  async deletePrinterModel(name) { try { const s = loadSettings(); const t=toKey(name); s.printerModels=(s.printerModels||[]).filter(x=>toKey(x)!==t); saveSettings(s); return { data: name, error: null }; } catch (error) { return { data: null, error }; } },
  async getPrinterModelsByBrand(brand) { try { const s = loadSettings(); const key = toKey(brand); const map = s.printerModelsByBrand || {}; const arr = Array.isArray(map[key])?map[key]:[]; return { data: arr, error: null }; } catch (error) { return { data: [], error }; } },
  async createPrinterModelForBrand(brand, modelName) { try { const s = loadSettings(); const b = normalizeCase(brand); const m = normalizeCase(modelName); const key = toKey(b); const map = s.printerModelsByBrand || {}; const arr = Array.isArray(map[key])?map[key]:[]; const updated = uniquePush(arr, m).sort((a,b)=>a.localeCompare(b)); map[key] = updated; s.printerModelsByBrand = map; saveSettings(s); return { data: map[key], error: null }; } catch (error) { return { data: [], error }; } },
  async updatePrinterModelForBrand(brand, oldName, newName) { try { const s = loadSettings(); const key = toKey(brand); const o = normalizeCase(oldName); const n = normalizeCase(newName); const map = s.printerModelsByBrand || {}; const arr = Array.isArray(map[key])?map[key]:[]; map[key] = arr.map(x=>toKey(x)===toKey(o)?n:normalizeCase(x)).sort((a,b)=>a.localeCompare(b)); s.printerModelsByBrand = map; saveSettings(s); return { data: map[key], error: null }; } catch (error) { return { data: [], error }; } },
  async deletePrinterModelForBrand(brand, name) { try { const s = loadSettings(); const key = toKey(brand); const t = toKey(name); const map = s.printerModelsByBrand || {}; const arr = Array.isArray(map[key])?map[key]:[]; map[key] = arr.filter(x=>toKey(x)!==t).sort((a,b)=>a.localeCompare(b)); s.printerModelsByBrand = map; saveSettings(s); return { data: map[key], error: null }; } catch (error) { return { data: [], error }; } },
  async mergePrinterModelsByBrand(modelsMap) { try { const s = loadSettings(); const dest = s.printerModelsByBrand || {}; const incoming = modelsMap || {}; Object.entries(incoming).forEach(([brand, models])=>{ const bKey = toKey(brand); const current = Array.isArray(dest[bKey])?dest[bKey]:[]; const map = new Map(current.map(v=>[toKey(v), normalizeCase(v)])); (Array.isArray(models)?models:[]).forEach(m=>{ const norm = normalizeCase(m); const key = toKey(norm); if (!map.has(key)) map.set(key, norm); }); dest[bKey] = Array.from(map.values()).sort((a,b)=>a.localeCompare(b)); }); s.printerModelsByBrand = dest; saveSettings(s); return { data: s.printerModelsByBrand, error: null }; } catch (error) { return { data: {}, error }; } },

  // Marcas de filamento
  async getFilamentBrands() { try { const s = loadSettings(); return { data: Array.isArray(s.filamentBrands)?s.filamentBrands:[], error: null }; } catch (error) { return { data: [], error }; } },
  async createFilamentBrand(name) { try { const s = loadSettings(); const n = normalizeCase(name); s.filamentBrands = uniquePush(s.filamentBrands, n); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; } },
  async updateFilamentBrand(oldName, newName) { try { const s = loadSettings(); const o=normalizeCase(oldName); const n=normalizeCase(newName); s.filamentBrands=(s.filamentBrands||[]).map(x=>toKey(x)===toKey(o)?n:normalizeCase(x)); saveSettings(s); return { data: n, error: null }; } catch (error) { return { data: null, error }; } },
  async deleteFilamentBrand(name) { try { const s = loadSettings(); const t=toKey(name); s.filamentBrands=(s.filamentBrands||[]).filter(x=>toKey(x)!==t); saveSettings(s); return { data: name, error: null }; } catch (error) { return { data: null, error }; } },
  async mergeFilamentBrands(list) { try { const s = loadSettings(); const current = Array.isArray(s.filamentBrands)?s.filamentBrands:[]; const map = new Map(current.map(v=>[toKey(v), normalizeCase(v)])); (Array.isArray(list)?list:[]).forEach(item=>{ const norm=normalizeCase(item); const key=toKey(norm); if(!map.has(key)) map.set(key,norm); }); s.filamentBrands = Array.from(map.values()).sort((a,b)=>a.localeCompare(b)); saveSettings(s); return { data: s.filamentBrands, error: null }; } catch (error) { return { data: [], error }; } },

  // Tipos de materiais com densidade
  async getMaterialTypes() { try { const s = loadSettings(); return { data: Array.isArray(s.materialTypes)?s.materialTypes:[], error: null }; } catch (error) { return { data: [], error }; } },
  async createMaterialType(type) { try { const s = loadSettings(); const name = normalizeCase(type?.name||''); const density = Number(type?.density); if (!name) return { data: null, error: new Error('Nome inválido') }; const exists = (s.materialTypes||[]).some(mt=>toKey(mt?.name)===toKey(name)); if (!exists) s.materialTypes=[...(s.materialTypes||[]), { name, density: Number.isFinite(density)?density:null }]; saveSettings(s); return { data: { name, density: Number.isFinite(density)?density:null }, error: null }; } catch (error) { return { data: null, error }; } },
  async updateMaterialType(oldName, updates) { try { const s = loadSettings(); const o=normalizeCase(oldName); s.materialTypes=(s.materialTypes||[]).map(mt=>toKey(mt?.name)===toKey(o)?{ name: normalizeCase(updates?.name||mt?.name||''), density: Number.isFinite(Number(updates?.density))?Number(updates?.density):mt?.density }: mt); saveSettings(s); return { data: updates, error: null }; } catch (error) { return { data: null, error }; } },
  async deleteMaterialType(name) { try { const s = loadSettings(); const t=toKey(name); s.materialTypes=(s.materialTypes||[]).filter(mt=>toKey(mt?.name)!==t); saveSettings(s); return { data: name, error: null }; } catch (error) { return { data: null, error }; } },
  async resetMaterialTypesToDefault() { try { const s = loadSettings(); const defaults = getDefaultSettings().materialTypes || []; s.materialTypes = defaults; saveSettings(s); return { data: s.materialTypes, error: null }; } catch (error) { return { data: [], error }; } },
};

export default settingsStore;